'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { getAnalyticsMetrics, type AnalyticsMetrics } from '@/services/analyticsService';
import { getAllClients, type Client } from '@/services/clientService';
import { getAllAppointments, type Appointment } from '@/services/appointmentService';
import { getPublishedBlogPosts, type BlogPost } from '@/services/blogService';
import { getAllBlogPosts } from '@/services/blogService';
import { getAllTestimonials, type ClientTestimonial } from '@/services/testimonialService';
import { getAllProcesses, type Process } from '@/services/processService';
import { getAllInvoices, type Invoice } from '@/services/invoiceService';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

export default function AdminDashboard() {
  const router = useRouter();
  const { user, signOut, loading } = useAuth();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsMetrics | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [allBlogPosts, setAllBlogPosts] = useState<BlogPost[]>([]);
  const [testimonials, setTestimonials] = useState<ClientTestimonial[]>([]);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      router.push('/admin/login');
    } else if (user) {
      loadDashboardData();
    }
  }, [user, loading, router]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const [analytics, clientsData, appointmentsData, blogData, allBlogData, testimonialsData, processesData, invoicesData] = await Promise.all([
        getAnalyticsMetrics(),
        getAllClients(),
        getAllAppointments(),
        getPublishedBlogPosts(5),
        getAllBlogPosts(),
        getAllTestimonials(),
        getAllProcesses(),
        getAllInvoices(),
      ]);

      setAnalyticsData(analytics);
      setClients(clientsData);
      setAppointments(appointmentsData);
      setBlogPosts(blogData);
      setAllBlogPosts(allBlogData);
      setTestimonials(testimonialsData);
      setProcesses(processesData);
      setInvoices(invoicesData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      router.push('/admin/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getRecentActivity = () => {
    const recentClients = clients.slice(0, 5).map((client) => ({
      type: 'client',
      name: client.companyName || client.userProfile?.fullName || 'N/A',
      date: new Date(client.createdAt).toLocaleDateString('pt-BR'),
      status: client.status,
    }));

    const recentAppointments = appointments.slice(0, 5).map((apt) => ({
      type: 'appointment',
      name: apt.title || 'Consulta',
      date: new Date(apt.scheduledAt).toLocaleDateString('pt-BR'),
      status: apt.status,
    }));

    return [...recentClients, ...recentAppointments]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 8);
  };

  const getChartData = () => {
    if (!analyticsData) return [];

    return [
      { name: 'Jan', acessos: 120, conversoes: 45 },
      { name: 'Fev', acessos: 180, conversoes: 67 },
      { name: 'Mar', acessos: 250, conversoes: 89 },
      { name: 'Abr', acessos: 320, conversoes: 112 },
      { name: 'Mai', acessos: 280, conversoes: 98 },
      { name: 'Jun', acessos: 390, conversoes: 145 },
    ];
  };

  const getProcessStatusCounts = () => {
    return {
      pending: processes.filter(p => p.status === 'pending').length,
      inProgress: processes.filter(p => p.status === 'in_progress').length,
      completed: processes.filter(p => p.status === 'completed').length,
      cancelled: processes.filter(p => p.status === 'cancelled').length,
    };
  };

  const getBlogPostsStats = () => {
    return {
      total: allBlogPosts.length,
      drafts: allBlogPosts.filter(p => !p.isPublished).length,
      published: allBlogPosts.filter(p => p.isPublished).length,
    };
  };

  const getInvoicesStats = () => {
    return {
      total: invoices.length,
      pending: invoices.filter(i => i.status === 'pending').length,
      paid: invoices.filter(i => i.status === 'paid').length,
      overdue: invoices.filter(i => i.status === 'overdue').length,
    };
  };

  const getAppointmentsStats = () => {
    return {
      total: appointments.length,
      scheduled: appointments.filter(a => a.status === 'scheduled').length,
      confirmed: appointments.filter(a => a.status === 'confirmed').length,
      completed: appointments.filter(a => a.isCompleted).length,
    };
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-obsidian-bg flex items-center justify-center">
        <div className="text-white text-xl">Carregando Dashboard...</div>
      </div>
    );
  }

  const processStats = getProcessStatusCounts();
  const blogStats = getBlogPostsStats();
  const invoiceStats = getInvoicesStats();
  const appointmentStats = getAppointmentsStats();
  const activeTestimonials = testimonials.filter(t => t.isActive).length;

  return (
    <div className="min-h-screen bg-obsidian-bg flex">
      <AdminSidebar activeSection="dashboard" onSectionChange={() => {}} onLogout={handleLogout} />

      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black text-white mb-2">
            Central Admin <span className="text-obsidian-accent">Hub</span>
          </h1>
          <p className="text-gray-400">Visão geral completa do sistema e gestão de conteúdo</p>
        </div>

        {/* Unified Statistical Widgets - Blog Posts */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
            <Icon name="DocumentTextIcon" className="w-6 h-6 text-obsidian-accent" />
            Blog Posts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-obsidian-accent transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="DocumentDuplicateIcon" className="w-10 h-10 text-blue-500" />
                <span className="text-3xl font-black text-white">{blogStats.total}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Total de Posts</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-yellow-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="PencilSquareIcon" className="w-10 h-10 text-yellow-500" />
                <span className="text-3xl font-black text-white">{blogStats.drafts}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Rascunhos</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-green-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="CheckCircleIcon" className="w-10 h-10 text-green-500" />
                <span className="text-3xl font-black text-white">{blogStats.published}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Publicados</h3>
            </div>
          </div>
        </div>

        {/* Unified Statistical Widgets - Clients & Appointments */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
            <Icon name="UsersIcon" className="w-6 h-6 text-obsidian-cyan" />
            Clientes & Agendamentos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-obsidian-cyan transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="UsersIcon" className="w-10 h-10 text-obsidian-cyan" />
                <span className="text-3xl font-black text-white">{clients.length}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Total de Clientes</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-green-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="CheckCircleIcon" className="w-10 h-10 text-green-500" />
                <span className="text-3xl font-black text-white">
                  {clients.filter((c) => c.status === 'approved').length}
                </span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Clientes Aprovados</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-obsidian-accent transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="CalendarIcon" className="w-10 h-10 text-obsidian-accent" />
                <span className="text-3xl font-black text-white">{appointmentStats.scheduled}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Agendamentos Programados</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-purple-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="CheckBadgeIcon" className="w-10 h-10 text-purple-500" />
                <span className="text-3xl font-black text-white">{appointmentStats.confirmed}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Agendamentos Confirmados</h3>
            </div>
          </div>
        </div>

        {/* Unified Statistical Widgets - Testimonials & Invoices */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
            <Icon name="StarIcon" className="w-6 h-6 text-orange-500" />
            Depoimentos & Faturas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-orange-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="ChatBubbleLeftRightIcon" className="w-10 h-10 text-orange-500" />
                <span className="text-3xl font-black text-white">{activeTestimonials}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Depoimentos Ativos</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-yellow-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="DocumentTextIcon" className="w-10 h-10 text-yellow-500" />
                <span className="text-3xl font-black text-white">{invoiceStats.pending}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Faturas Pendentes</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-green-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="CheckCircleIcon" className="w-10 h-10 text-green-500" />
                <span className="text-3xl font-black text-white">{invoiceStats.paid}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Faturas Pagas</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-red-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="ExclamationTriangleIcon" className="w-10 h-10 text-red-500" />
                <span className="text-3xl font-black text-white">{invoiceStats.overdue}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Faturas Vencidas</h3>
            </div>
          </div>
        </div>

        {/* Unified Statistical Widgets - Case Management */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
            <Icon name="BriefcaseIcon" className="w-6 h-6 text-purple-500" />
            Gestão de Processos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-purple-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="FolderIcon" className="w-10 h-10 text-purple-500" />
                <span className="text-3xl font-black text-white">{processes.length}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Total de Processos</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-yellow-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="ClockIcon" className="w-10 h-10 text-yellow-500" />
                <span className="text-3xl font-black text-white">{processStats.pending}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Processos Pendentes</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-blue-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="ArrowPathIcon" className="w-10 h-10 text-blue-500" />
                <span className="text-3xl font-black text-white">{processStats.inProgress}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Processos em Andamento</h3>
            </div>

            <div
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-green-500 transition-all duration-300"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon name="CheckCircleIcon" className="w-10 h-10 text-green-500" />
                <span className="text-3xl font-black text-white">{processStats.completed}</span>
              </div>
              <h3 className="text-gray-400 text-sm uppercase tracking-wider">Processos Concluídos</h3>
            </div>
          </div>
        </div>

        {/* Quick Action Navigation Cards */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
            <Icon name="BoltIcon" className="w-6 h-6 text-obsidian-accent" />
            Ações Rápidas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Blog Management Card */}
            <button
              onClick={() => router.push('/admin/blog')}
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-obsidian-accent transition-all duration-300 text-left group"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-obsidian-accent/20 flex items-center justify-center group-hover:bg-obsidian-accent/30 transition-all">
                  <Icon name="DocumentTextIcon" className="w-6 h-6 text-obsidian-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-obsidian-accent transition-colors">
                    Gerenciar Blog
                  </h3>
                  <p className="text-gray-400 text-sm mb-3">
                    Criar, editar e publicar posts do blog. Gerencie categorias e SEO.
                  </p>
                  <div className="flex items-center gap-2 text-obsidian-accent text-sm font-semibold">
                    <span>Acessar Blog</span>
                    <Icon name="ArrowRightIcon" className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </button>

            {/* Testimonials Management Card */}
            <button
              onClick={() => router.push('/admin/testimonials')}
              className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6 hover:border-obsidian-cyan transition-all duration-300 text-left group"
              style={{
                backdropFilter: 'blur(15px)',
                background: 'rgba(21, 27, 38, 0.8)',
              }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-obsidian-cyan/20 flex items-center justify-center group-hover:bg-obsidian-cyan/30 transition-all">
                  <Icon name="ChatBubbleLeftRightIcon" className="w-6 h-6 text-obsidian-cyan" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-obsidian-cyan transition-colors">
                    Gerenciar Depoimentos
                  </h3>
                  <p className="text-gray-400 text-sm mb-3">
                    Adicionar, editar e organizar depoimentos de clientes satisfeitos.
                  </p>
                  <div className="flex items-center gap-2 text-obsidian-cyan text-sm font-semibold">
                    <span>Acessar Depoimentos</span>
                    <Icon name="ArrowRightIcon" className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Content Overview Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Recent Blog Posts Overview */}
          <div
            className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6"
            style={{
              backdropFilter: 'blur(15px)',
              background: 'rgba(21, 27, 38, 0.8)',
            }}
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Icon name="NewspaperIcon" className="w-6 h-6 text-obsidian-accent" />
              Posts Recentes do Blog
            </h2>
            <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar">
              {blogPosts.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Icon name="DocumentTextIcon" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum post publicado ainda</p>
                </div>
              ) : (
                blogPosts.map((post) => (
                  <div
                    key={post.id}
                    className="flex items-start gap-4 p-4 bg-obsidian-bg rounded-lg border border-obsidian-border hover:border-obsidian-accent transition-all duration-300 cursor-pointer"
                    onClick={() => router.push(`/blog/${post.slug}`)}
                  >
                    {post.featuredImage && (
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={post.featuredImage}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold mb-1 truncate">{post.title}</h3>
                      <p className="text-gray-400 text-sm line-clamp-2">{post.excerpt || 'Sem descrição'}</p>
                      <div className="flex items-center gap-3 mt-2">
                        {post.category && (
                          <span className="text-xs px-2 py-1 bg-obsidian-accent/20 text-obsidian-accent rounded">
                            {post.category}
                          </span>
                        )}
                        {post.publishedAt && (
                          <span className="text-xs text-gray-500">
                            {new Date(post.publishedAt).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {blogPosts.length > 0 && (
              <button
                onClick={() => router.push('/admin/blog')}
                className="w-full mt-4 py-2 text-obsidian-accent hover:text-white border border-obsidian-accent hover:bg-obsidian-accent transition-all rounded-lg font-semibold"
              >
                Ver Todos os Posts
              </button>
            )}
          </div>

          {/* Testimonials Overview */}
          <div
            className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6"
            style={{
              backdropFilter: 'blur(15px)',
              background: 'rgba(21, 27, 38, 0.8)',
            }}
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Icon name="StarIcon" className="w-6 h-6 text-obsidian-cyan" />
              Depoimentos de Clientes
            </h2>
            <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar">
              {testimonials.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Icon name="ChatBubbleLeftRightIcon" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum depoimento cadastrado ainda</p>
                </div>
              ) : (
                testimonials.slice(0, 5).map((testimonial) => (
                  <div
                    key={testimonial.id}
                    className="flex items-start gap-4 p-4 bg-obsidian-bg rounded-lg border border-obsidian-border hover:border-obsidian-cyan transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-obsidian-surface">
                      <img
                        src={testimonial.logoUrl}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-semibold">{testimonial.name}</h3>
                        {testimonial.isActive && (
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-500 rounded">
                            Ativo
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-2">{testimonial.testimonial}</p>
                      {testimonial.city && testimonial.state && (
                        <p className="text-xs text-gray-500 mt-1">
                          {testimonial.city}, {testimonial.state} - {testimonial.country}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            {testimonials.length > 0 && (
              <button
                onClick={() => router.push('/admin/testimonials')}
                className="w-full mt-4 py-2 text-obsidian-cyan hover:text-white border border-obsidian-cyan hover:bg-obsidian-cyan transition-all rounded-lg font-semibold"
              >
                Ver Todos os Depoimentos
              </button>
            )}
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Line Chart - Analytics */}
          <div
            className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6"
            style={{
              backdropFilter: 'blur(15px)',
              background: 'rgba(21, 27, 38, 0.8)',
            }}
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Icon name="ChartBarIcon" className="w-6 h-6 text-obsidian-accent" />
              Analytics - Acessos e Conversões
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={getChartData()}>
                <defs>
                  <linearGradient id="colorAcessos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff0000" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#ff0000" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorConversoes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00d9ff" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#00d9ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a3441" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#151b26',
                    border: '1px solid #2a3441',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="acessos"
                  stroke="#ff0000"
                  strokeWidth={3}
                  fill="url(#colorAcessos)"
                />
                <Line
                  type="monotone"
                  dataKey="conversoes"
                  stroke="#00d9ff"
                  strokeWidth={3}
                  fill="url(#colorConversoes)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Recent Activity */}
          <div
            className="bg-obsidian-surface border border-obsidian-border rounded-lg p-6"
            style={{
              backdropFilter: 'blur(15px)',
              background: 'rgba(21, 27, 38, 0.8)',
            }}
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Icon name="BellIcon" className="w-6 h-6 text-obsidian-accent" />
              Atividade Recente
            </h2>
            <div className="space-y-4 max-h-[300px] overflow-y-auto custom-scrollbar">
              {getRecentActivity().map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-3 bg-obsidian-bg rounded-lg border border-obsidian-border hover:border-obsidian-cyan transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-full bg-obsidian-accent/20 flex items-center justify-center">
                    <Icon
                      name={activity.type === 'client' ? 'UserIcon' : 'CalendarIcon'}
                      className="w-5 h-5 text-obsidian-accent"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold text-sm">{activity.name}</p>
                    <p className="text-gray-400 text-xs">{activity.date}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      activity.status === 'approved' || activity.status === 'confirmed' ?'bg-green-500/20 text-green-500'
                        : activity.status === 'pending' ?'bg-yellow-500/20 text-yellow-500' :'bg-gray-500/20 text-gray-500'
                    }`}
                  >
                    {activity.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}